import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, DollarSign, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import ProjectCard from '../components/dashboard/ProjectCard';
import RecentActivity from '../components/dashboard/RecentActivity';
import { useProjects } from '../hooks/useProjects';
import { useExpenses } from '../hooks/useExpenses';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: expenses, isLoading: expensesLoading } = useExpenses();

  const loading = projectsLoading || expensesLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const activeProjects = projects?.filter((p) => p.status === 'active') || [];
  const totalBudget = activeProjects.reduce((sum, p) => sum + p.budget, 0);
  const totalSpent = activeProjects.reduce((sum, p) => sum + p.spent, 0);
  const overBudgetCount = activeProjects.filter(
    (p) => p.budgetStatus === 'over'
  ).length;

  // Sort by most spent and limit to 6 projects
  const displayedProjects = activeProjects
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 6);
  const hasMoreProjects = activeProjects.length > 6;

  // Sort expenses by date descending (most recent first) and take top 5
  const recentExpenses = expenses?.items
    ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5) || [];

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Resumen de proyectos y actividad reciente
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Proyectos Activos
                </p>
                <p className="text-2xl font-bold">
                  {activeProjects.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 rounded-lg p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Presupuesto Total
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(totalBudget)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 rounded-lg p-3">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Gastado
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(totalSpent)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div
                className={`rounded-lg p-3 ${
                  overBudgetCount > 0 ? 'bg-red-100' : 'bg-green-100'
                }`}
              >
                {overBudgetCount > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Sobre Presupuesto
                </p>
                <p className="text-2xl font-bold">
                  {overBudgetCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Proyectos Activos
          </h2>
          {hasMoreProjects && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
            >
              Ver todos ({activeProjects.length})
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {displayedProjects.map((project) => (
            <ProjectCard key={project.projectId} project={project} />
          ))}
        </div>
        {activeProjects.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No hay proyectos activos
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <RecentActivity expenses={recentExpenses} maxItems={5} />
      </div>
    </div>
  );
};

export default Dashboard;
